/**
 * WebSocket Manager - Phase 3 Architecture
 *
 * Simplified manager for tracking active chat and heartbeat activity.
 * Phase 3 uses a single WebSocket connection per user (managed by websocketService).
 *
 * This manager only handles:
 * - Active chat tracking (which chat is currently visible)
 * - Heartbeat timestamp tracking per chat
 *
 * @see websocketService.ts for WebSocket connection management
 */

interface ChatActivity {
    lastHeartbeat: number;
}

class WebSocketManager {
    private chatActivity: Map<string, ChatActivity> = new Map();
    private activeChat: string | null = null;

    // ========== Public API ==========

    /**
     * Set the currently active chat (visible in UI)
     */
    public setActiveChat(chatId: string | null) {
        this.activeChat = chatId;

        if (chatId) {
            // Initialize activity tracking for active chat
            if (!this.chatActivity.has(chatId)) {
                this.chatActivity.set(chatId, {
                    lastHeartbeat: Date.now()
                });
            }
        }
    }

    /**
     * Get the currently active chat
     */
    public getActiveChat(): string | null {
        return this.activeChat;
    }

    /**
     * Called when user-level WebSocket connection is opened
     */
    public onUserConnectionOpened(_ws: WebSocket) {
        // Connection is managed by websocketService
    }

    /**
     * Update heartbeat timestamp for a chat
     * Called when we receive a message for that chat
     */
    public updateHeartbeat(chatId: string): void {
        const activity = this.chatActivity.get(chatId);
        if (activity) {
            activity.lastHeartbeat = Date.now();
        } else {
            this.chatActivity.set(chatId, {
                lastHeartbeat: Date.now()
            });
        }
    }

    /**
     * Get tracking statistics (for debugging)
     */
    public getStats() {
        return {
            activeChat: this.activeChat,
            trackedChats: this.chatActivity.size,
            chats: Array.from(this.chatActivity.entries()).map(([chatId, activity]) => ({
                chatId,
                lastHeartbeat: new Date(activity.lastHeartbeat).toISOString()
            }))
        };
    }

    /**
     * Cleanup all tracking data
     */
    public cleanup() {
        this.chatActivity.clear();
        this.activeChat = null;
    }
}

// Export singleton instance
export const wsManager = new WebSocketManager();

// Expose for debugging in development
if (typeof window !== 'undefined' && import.meta.env.MODE === 'development') {
    (window as any).wsManager = wsManager;
}
